import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Grid, Space, Typography } from 'antd';
import { AuditOutlined, ExperimentOutlined, ReloadOutlined } from '@ant-design/icons';
import { PageLoader } from '@shared/common/components/ui/busy/busy-indicator';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { getScenarioReviewQueue, type ScenarioReviewItem, type ScenarioReviewQueue } from '@/api/scenarioVersions';
import { ScenarioLibraryPage } from '@/features/validations/ScenarioLibraryPage';
import { QuickReviewDrawer } from '../review/QuickReviewDrawer';
import { AdvancedScenarioEditorDrawer } from '../editor/AdvancedScenarioEditorDrawer';
import { PART_WORDS, POLICY_WORDS } from '../scenarioWords';
import { CoverageCard, REQUIREMENT_NOTE } from './CoverageCard';
import { useWorkspace } from './EvaluationWorkspace';

const { Text } = Typography;
const MONO = "'JetBrains Mono', monospace";

// ─────────────────────────────────────────────────────────────────────────────
// The workspace tabs that are not the Overview. Each reuses what exists: the Overview's coverage on its own, the
// scenario library (narrowed to the workspace's agent), and screen 05's review queue. Runs and Compare are SI-03b's
// and SI-07's pages, routed as they are (routes.tsx).
// ─────────────────────────────────────────────────────────────────────────────

/** Without an agent, a tab that needs one says so and offers the picker. */
const NeedsAgent = observer(function NeedsAgent({ what }: { what: string }) {
  const ws = useWorkspace();
  if (ws.resolving)
    return (
      <SectionCard>
        <PageLoader label={`Finding the agent last evaluated in ${ws.practiceLabel}`} />
      </SectionCard>
    );
  return (
    <SectionCard>
      <BrandedEmpty
        icon={<ExperimentOutlined />}
        title="Choose an agent to evaluate"
        description={`${what} is shown for one agent at a time. Any agent of ${ws.practiceLabel}, or a shared agent, can be evaluated here.`}
        action={
          <Button type="primary" onClick={ws.chooseAgent}>
            Choose an agent…
          </Button>
        }
      />
    </SectionCard>
  );
});

export const CoverageTab = observer(function CoverageTab() {
  const ws = useWorkspace();
  if (ws.agentId == null) return <NeedsAgent what="Coverage" />;
  const { data, error } = ws.worklist;
  if (!data && error)
    return (
      <Alert
        type="error"
        showIcon
        title="Could not load the coverage"
        description={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span>{`The server said: "${error}"`}</span>
            <Space>
              <Button size="small" icon={<ReloadOutlined />} onClick={ws.reload}>Retry</Button>
              <Button size="small" onClick={ws.chooseAgent}>Choose an agent…</Button>
            </Space>
          </div>
        }
      />
    );
  if (!data)
    return (
      <SectionCard>
        <PageLoader label="Loading the coverage" hint="Counting the library's scenarios per category, each in its state on its current version." />
      </SectionCard>
    );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="si-coverage-tab">
      <Text type="secondary" style={{ fontSize: 12.5 }}>
        {`${data.agentName} in ${ws.practiceLabel}. `}
        {REQUIREMENT_NOTE}
      </Text>
      <CoverageCard data={data} />
    </div>
  );
});

/**
 * The scenario library, narrowed to the workspace's agent. /evaluation/scenarios/:id narrows it to that scenario by
 * search, so a link to one scenario lands on its card.
 */
export const ScenariosTab = observer(function ScenariosTab() {
  const ws = useWorkspace();
  const { scenariosStore } = useStore();
  const { id } = useParams<{ id?: string }>();
  useEffect(() => {
    if (ws.agentId != null && scenariosStore.params.agentId !== ws.agentId) scenariosStore.setAgentFilter(ws.agentId);
  }, [ws.agentId, scenariosStore]);
  useEffect(() => {
    if (id) scenariosStore.setSearch(id);
  }, [id, scenariosStore]);
  return <ScenarioLibraryPage />;
});

/** Screen 05's review queue for the workspace's agent: what waits for a decision, grouped by the server's policy. */
export const ReviewsTab = observer(function ReviewsTab() {
  const ws = useWorkspace();
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const narrow = !Grid.useBreakpoint().md;
  const [queue, setQueue] = useState<ScenarioReviewQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<{ open: boolean; scenarioId: string | null }>({ open: false, scenarioId: null });
  const [editing, setEditing] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    setQueue(null);
    setError(null);
    if (ws.agentId == null) return;
    let cancelled = false;
    getScenarioReviewQueue(ws.agentId)
      .then((q) => !cancelled && setQueue(q))
      .catch((err: Error) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [ws.agentId, version]);

  if (ws.agentId == null) return <NeedsAgent what="The review queue" />;
  const name = ws.agent?.canonicalName || ws.agent?.displayName || `agent #${ws.agentId}`;
  const waiting = queue?.items.filter((i) => i.decision == null) ?? [];
  const openQueue = (
    <Button icon={<AuditOutlined />} type="primary" onClick={() => setOpen({ open: true, scenarioId: null })}>
      Open the review queue
    </Button>
  );
  const groups = [
    { key: 'one', title: 'One at a time', hint: 'checks, context or what the user says changed, or never approved', rows: waiting.filter((i) => i.policy !== 'WordingOnly') },
    { key: 'wording', title: 'Wording only', hint: 'batch allowed', rows: waiting.filter((i) => i.policy === 'WordingOnly') },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="si-reviews-tab">
      {error && (
        <Alert
          type="error"
          showIcon
          title="Could not load the review queue"
          description={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span>{`The server said: "${error}"`}</span>
              <Space>
                <Button size="small" icon={<ReloadOutlined />} onClick={() => setVersion((v) => v + 1)}>Retry</Button>
              </Space>
            </div>
          }
        />
      )}
      {!queue && !error && (
        <SectionCard>
          <PageLoader label={`Loading the review queue for ${name}`} hint="Each scenario's current version, what changed since the version last approved, and what blocks an approval." />
        </SectionCard>
      )}
      {queue && (
        <SectionCard
          title={`${waiting.length} version${waiting.length === 1 ? '' : 's'} waiting for a decision`}
          subtitle="Scenario approvals are bound to a version's content hash; an edit makes a new version that needs its own decision. Release approvals are recorded on the agent's Evaluation tab in Agent Design."
          actions={narrow ? undefined : openQueue}
        >
          {/* On a phone the button takes its own row, so the heading is never squeezed beside it. */}
          {narrow && <div style={{ marginBottom: 12 }}>{openQueue}</div>}
          {waiting.length === 0 ? (
            <BrandedEmpty
              icon={<AuditOutlined />}
              title={`Nothing waits for a decision for ${name}`}
              description={`Every active scenario's current version has a recorded decision (${queue.items.length} in all). Decided versions stay in the queue under All.`}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {groups
                .filter((g) => g.rows.length > 0)
                .map((g) => (
                  <div key={g.key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                      <StatusPill tone={g.key === 'wording' ? 'violet' : 'amber'}>{g.title}</StatusPill>
                      <Text type="secondary" style={{ fontSize: 12 }}>{`${g.rows.length} · ${g.hint}`}</Text>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {g.rows.map((i) => (
                        <ReviewRow key={i.scenarioId} item={i} isDark={isDark} onOpen={() => setOpen({ open: true, scenarioId: i.scenarioId })} />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 12 }}>
            {'Old links to Approvals land here. '}
            {ws.agent && <Link to={`/agents/${ws.agentId}`}>{`Open ${ws.agent.displayName} in Agent Design`}</Link>}
          </Text>
        </SectionCard>
      )}
      <QuickReviewDrawer
        open={open.open}
        onClose={() => {
          setOpen({ open: false, scenarioId: null });
          setVersion((v) => v + 1);
          ws.reload();
        }}
        agentId={ws.agentId}
        agentName={name}
        initialScenarioId={open.scenarioId}
        onOpenScenario={setEditing}
      />
      <AdvancedScenarioEditorDrawer
        open={editing != null}
        scenarioId={editing}
        onClose={() => setEditing(null)}
        onSaved={() => setVersion((v) => v + 1)}
        onOpenScenario={setEditing}
      />
    </div>
  );
});

function ReviewRow({ item, isDark, onOpen }: { item: ScenarioReviewItem; isDark: boolean; onOpen: () => void }) {
  const changed = item.changedParts.map((p) => PART_WORDS[p]).join(', ');
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '6px 12px',
        padding: '10px 14px',
        borderRadius: 12,
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        background: isDark ? colors.slate[800] : '#FFFFFF',
      }}
    >
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, overflowWrap: 'anywhere' }}>{item.scenarioId}</div>
        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
          {`Version ${item.current.number} · ${POLICY_WORDS[item.policy].short}${changed ? ` · changed: ${changed}` : ''}`}
          {item.blockers.length > 0 ? ` · ${item.blockers.length} thing${item.blockers.length === 1 ? '' : 's'} block${item.blockers.length === 1 ? 's' : ''} an approval` : ''}
          {item.authoredByViewer ? ' · you wrote this version' : ''}
        </Text>
      </div>
      <Button size="small" onClick={onOpen}>
        Review
      </Button>
    </div>
  );
}
